import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Sidebar } from '../../../../common/components/Sidebar';
import SectionBar from '../../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../../hooks';
import { useDesignatedOperators } from '../../../Profile/hooks/useDesignatedOperators';
import {
  useEditRemunerationGroups,
  useRemunerationGroupOptions,
  useExchangeOfficeOptions,
  useSaveRemunerationGroupFragments,
  useSaveRemunerationGroupFragmentsWithoutConfirmation,
  type EditRemunerationGroupRow,
  type RemunerationGroupOption,
  type ExchangeOfficeOption,
  type ExchangeOffice,
} from '../hooks';
import { RemunerationGroupsTable } from './RemunerationGroupsTable';
import NotificationSnackbar from '../../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../../common/components/NoDataFound';
import {
  MainContent,
  ReportContainer,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  ClearResultsButton,
  SearchButton,
} from '../RemunerationGroups.styled';

function EditRemunerationGroups() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedYear, setSelectedYear] = useState('');
  const [selectedRemunerationGroup, setSelectedRemunerationGroup] = useState('');
  const [selectedReceivingPpo, setSelectedReceivingPpo] = useState('');

  // Add state for results
  const [results, setResults] = useState<EditRemunerationGroupRow | null>(null);
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const [isAutoSearching, setIsAutoSearching] = useState<boolean>(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    message: '',
    pendingSaveData: null as any,
  });

  // Error modal state for duplicate entries
  const [errorModal, setErrorModal] = useState({
    open: false,
    message: '',
  });

  // Fetch designated operators for PPO dropdown
  const {
    data: designatedOperatorsData,
    isLoading: isPpoLoading,
    error: ppoError,
  } = useDesignatedOperators({ size: 300 });

  // Fetch remuneration group options
  const {
    data: remunerationGroupOptionsData,
    isLoading: isRemunerationGroupLoading,
    error: remunerationGroupError,
  } = useRemunerationGroupOptions();

  // Fetch exchange office options
  const {
    data: exchangeOfficeOptionsData,
    isLoading: isExchangeOfficeLoading,
    error: exchangeOfficeError,
  } = useExchangeOfficeOptions();

  // Edit remuneration groups hook
  const {
    mutate: searchEditRemunerationGroups,
    isPending: isSearching,
    error: searchError,
    reset: resetSearchError,
  } = useEditRemunerationGroups();

  // Save remuneration group fragments hook
  const {
    mutate: saveRemunerationGroupFragments,
    isPending: isSaving,
    error: saveError,
    reset: resetSaveError,
  } = useSaveRemunerationGroupFragments();

  // Save without confirmation hook
  const {
    mutate: saveWithoutConfirmation,
    isPending: isSavingWithoutConfirmation,
    error: saveWithoutConfirmationError,
    reset: resetSaveWithoutConfirmationError,
  } = useSaveRemunerationGroupFragmentsWithoutConfirmation();

  // Generate year options from 2008 to current year
  const yearOptions = React.useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const currentYear = new Date().getFullYear();

    for (let year = currentYear; year >= 2008; year--) {
      options.push({
        value: year.toString(),
        label: year.toString(),
      });
    }

    return options;
  }, []);

  // Use remuneration group options from hook, but prioritize navigation state if available
  const remunerationGroupOptions = React.useMemo(() => {
    const navigationState = location.state as any;
    
    // If we have remuneration group data from navigation state, create a custom option
    if (navigationState?.remunerationGroup && typeof navigationState.remunerationGroup === 'object') {
      const navRemunerationGroup = navigationState.remunerationGroup;
      const customOption = {
        id: navRemunerationGroup.id?.toString() || navRemunerationGroup.name,
        label: navRemunerationGroup.displayName || navRemunerationGroup.name,
      };
      console.log('Creating custom remuneration group option:', customOption);
      return [
        customOption,
        ...(remunerationGroupOptionsData || [])
      ];
    }
    
    return remunerationGroupOptionsData || [];
  }, [remunerationGroupOptionsData, location.state]);

  // Map the designated operators to PPO options
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];

    if (designatedOperatorsData?.content) {
      designatedOperatorsData.content
        .filter((operator) => !operator.disabled)
        .forEach((operator) => {
          options.push({
            id: operator.name,
            label: operator.name,
            hasLicense: operator.longName !== null,
          });
        });
    }

    return options;
  }, [designatedOperatorsData]);

  // Handler for quarter checkbox changes
  const handleQuarterChange = (
    exchangeOffice: ExchangeOffice,
    quarter: keyof Pick<ExchangeOffice, 'quarter1' | 'quarter2' | 'quarter3' | 'quarter4'>,
    value: boolean
  ) => {
    if (!results) return;

    // Prevent changes if "not subject to sampling" is enabled
    if (results.notSubjToSampling) return;

    const updatedResults = {
      ...results,
      exchangeOffices: results.exchangeOffices.map((eo) =>
        eo === exchangeOffice ? { ...eo, [quarter]: value } : eo
      ),
    };
    setResults(updatedResults);
  };

  // Handler for PPO changes
  const handlePpoChange = (exchangeOffice: ExchangeOffice, newPpo: string) => {
    if (!results) return;

    const updatedResults = {
      ...results,
      exchangeOffices: results.exchangeOffices.map((eo) =>
        eo === exchangeOffice ? { ...eo, newPpo } : eo
      ),
    };
    setResults(updatedResults);
  };

  // Handler for exchange office changes
  const handleExchangeOfficeChange = (
    exchangeOffice: ExchangeOffice,
    newExchangeOffice: string
  ) => {
    if (!results) return;

    const updatedResults = {
      ...results,
      exchangeOffices: results.exchangeOffices.map((eo) =>
        eo === exchangeOffice ? { ...eo, newExchangeOffice } : eo
      ),
    };
    setResults(updatedResults);
  };

  // Handler for not subject to sampling checkbox
  const handleNotSubjToSamplingChange = (value: boolean) => {
    if (!results) return;

    // If enabling "not subject to sampling", set all quarters to true
    const updatedExchangeOffices = value 
      ? results.exchangeOffices.map(eo => ({
          ...eo,
          quarter1: true,
          quarter2: true,
          quarter3: true,
          quarter4: true,
        }))
      : results.exchangeOffices;

    const updatedResults = {
      ...results,
      notSubjToSampling: value,
      exchangeOffices: updatedExchangeOffices,
    };
    setResults(updatedResults);
  };

  // Handler for adding new rows
  const handleAddNewRow = (newRow: ExchangeOffice) => {
    if (!results) {
      // Validate required fields before creating initial results
      if (!selectedYear) {
        showSnackbar('Please select a Year before adding exchange offices.', 'warning');
        return;
      }
      if (!selectedRemunerationGroup) {
        showSnackbar('Please select a Remuneration Group before adding exchange offices.', 'warning');
        return;
      }
      if (!selectedReceivingPpo) {
        showSnackbar('Please select a Receiving DO before adding exchange offices.', 'warning');
        return;
      }

      // Create initial results structure if it doesn't exist
      const initialResults: EditRemunerationGroupRow = {
        rgId: parseInt(selectedRemunerationGroup) || 0,
        year: parseInt(selectedYear),
        receivingPpo: selectedReceivingPpo,
        exchangeOffices: [newRow],
        notSubjToSampling: false,
      };
      setResults(initialResults);
      return;
    }

    // Check for duplicate DO and Exchange Office combination
    const isDuplicate = results.exchangeOffices.some(
      (existingRow) => 
        existingRow.ppo === newRow.ppo && 
        existingRow.exchangeOffice === newRow.exchangeOffice
    );

    if (isDuplicate) {
      setErrorModal({
        open: true,
        message: 'This DO and IMPC is already present.',
      });
      return;
    }

    const updatedResults = {
      ...results,
      exchangeOffices: [...results.exchangeOffices, newRow],
    };
    setResults(updatedResults);
  };

  // Handler for removing rows
  const handleRemoveRow = (rowToRemove: ExchangeOffice) => {
    if (!results) return;

    const updatedResults = {
      ...results,
      exchangeOffices: results.exchangeOffices.filter((eo) => eo !== rowToRemove),
    };
    setResults(updatedResults);
  };

  // Handler for saving changes
  const handleSave = (exchangeOffices: ExchangeOffice[]) => {
    if (!results) {
      showSnackbar('No data to save. Please add some exchange offices first.', 'warning');
      return;
    }

    // Transform exchange offices to the new API format
    console.log('Original exchange offices before transformation:', exchangeOffices);
    
    const transformedExchangeOffices = exchangeOffices.map((eo, index) => {
      // Calculate quarters array based on selected quarters
      const quarters: string[] = [];
      if (eo.quarter1) quarters.push('ONE');
      if (eo.quarter2) quarters.push('TWO');
      if (eo.quarter3) quarters.push('THREE');
      if (eo.quarter4) quarters.push('FOUR');

      const transformed = {
        sendingPpo: eo.newPpo || eo.ppo,
        exchangeOffice: eo.newExchangeOffice || eo.exchangeOffice,
        quarter1: eo.quarter1,
        quarter2: eo.quarter2,
        quarter3: eo.quarter3,
        quarter4: eo.quarter4,
        quarters: quarters,
      };

      console.log(`Transformed exchange office ${index}:`, {
        original: eo,
        transformed: transformed
      });

      return transformed;
    });

    console.log('All transformed exchange offices:', transformedExchangeOffices);

    const saveData = {
      id: results.rgId,
      year: results.year,
      receivingPpo: results.receivingPpo,
      notSubjToSampling: results.notSubjToSampling,
      upuRgSenderExchangeOfficeDtos: transformedExchangeOffices,
    };

    console.log('Saving data in new API format:', saveData);

    saveRemunerationGroupFragments(saveData, {
      onSuccess: (response) => {
        handleSaveResponse(response, saveData);
      },
      onError: (error) => {
        console.error('Error saving remuneration group fragments:', error);
        showSnackbar('Failed to save changes. Please try again.');
      },
    });
  };

  // Handler for processing save response
  const handleSaveResponse = (response: any, saveData: any) => {
    console.log('Save response:', response);

    // Handle error messages
    if (response.errorMessages && response.errorMessages.length > 0) {
      const errorMessage = response.errorMessages.join(', ');
      showSnackbar(errorMessage, 'error');
      return;
    }

    // Handle confirmation requirement
    if (response.confirmation && !response.ok) {
      setConfirmationModal({
        open: true,
        message: response.confirmationMessage || 'Do you want to proceed with this action?',
        pendingSaveData: saveData,
      });
      return;
    }

    // Handle success
    if (response.ok) {
      showSnackbar('Changes saved successfully!', 'success');
    }
  };

  // Handler for confirmed save (with confirmation disabled)
  const handleConfirmedSave = () => {
    if (!confirmationModal.pendingSaveData) return;

    saveWithoutConfirmation(confirmationModal.pendingSaveData, {
      onSuccess: (response) => {
        setConfirmationModal({ open: false, message: '', pendingSaveData: null });
        if (response.errorMessages && response.errorMessages.length > 0) {
          const errorMessage = response.errorMessages.join(', ');
          showSnackbar(errorMessage, 'error');
        } else {
          showSnackbar('Changes saved successfully!', 'success');
        }
      },
      onError: (error) => {
        console.error('Error saving with confirmation disabled:', error);
        setConfirmationModal({ open: false, message: '', pendingSaveData: null });
        showSnackbar('Failed to save changes. Please try again.', 'error');
      },
    });
  };

  // Handler for canceling confirmation
  const handleCancelConfirmation = () => {
    setConfirmationModal({ open: false, message: '', pendingSaveData: null });
  };

  // Handler for closing error modal
  const handleCloseErrorModal = () => {
    setErrorModal({ open: false, message: '' });
  };

  // No need for table columns definition with the new custom table

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/upu-remuneration-groups');
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleReset = () => {
    setSelectedYear('');
    setSelectedRemunerationGroup('');
    setSelectedReceivingPpo('');
    setResults(null);
    setNoDataFound(false);
    setSearchParams(new URLSearchParams());
  };

  const handleSearch = () => {
    // Reset any previous errors
    resetSearchError();
    setNoDataFound(false);

    // Validate required fields
    if (!selectedYear) {
      showSnackbar('Please select a Year');
      return;
    }

    if (!selectedRemunerationGroup) {
      showSnackbar('Please select a Remuneration Group');
      return;
    }

    if (!selectedReceivingPpo) {
      showSnackbar('Please select a Receiving DO');
      return;
    }

    // Prepare API parameters
    const params = {
      year: parseInt(selectedYear),
      remunerationGroup: selectedRemunerationGroup,
      receivingPpo: selectedReceivingPpo,
    };

    searchEditRemunerationGroups(params, {
      onSuccess: (response) => {
        console.log('Edit Remuneration Groups search results:', response.data);
        console.log('Current filter params used:', params);

        // Handle new API structure with upuRgSenderExchangeOfficeDtos
        if (response.data) {
          console.log('Raw API response data:', response.data);
          
          // Convert upuRgSenderExchangeOfficeDtos to exchangeOffices format
          const exchangeOfficesData = (response.data as any).upuRgSenderExchangeOfficeDtos || [];
          console.log('Exchange offices from API:', exchangeOfficesData);
          
          // Transform the data to match expected ExchangeOffice interface
          const transformedExchangeOffices = exchangeOfficesData.map((item: any) => ({
            ppo: item.ppo || item.sendingPpo || '-',
            exchangeOffice: item.exchangeOffice || item.exchangeOfficeName || '-',
            quarter1: item.quarter1 ?? false,
            quarter2: item.quarter2 ?? false,
            quarter3: item.quarter3 ?? false,
            quarter4: item.quarter4 ?? false,
            newPpo: item.newPpo || item.ppo || item.sendingPpo || '',
            newExchangeOffice: item.newExchangeOffice || item.exchangeOffice || item.exchangeOfficeName || '',
          }));

          console.log('Transformed exchange offices:', transformedExchangeOffices);

          // Create the expected data structure
          const transformedData = {
            rgId: (response.data as any).rgId || (response.data as any).id || 0,
            year: response.data.year || new Date().getFullYear(),
            receivingPpo: response.data.receivingPpo || '',
            exchangeOffices: transformedExchangeOffices,
            notSubjToSampling: response.data.notSubjToSampling ?? false,
          };

          console.log('Final transformed data:', transformedData);

          // Always set the results - empty exchange offices array is valid
          // This allows users to add new rows even when starting with empty data
          setNoDataFound(false);
          setResults(transformedData);
        } else {
          setNoDataFound(true);
          setResults(null);
        }
      },
      onError: (error) => {
        console.error('Error searching edit remuneration groups:', error);
      },
    });
  };

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setResults(null);
    setNoDataFound(false);
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Auto-search function for when coming from main page
  const handleAutoSearch = useCallback(() => {
    if (!selectedYear || !selectedRemunerationGroup || !selectedReceivingPpo) {
      return;
    }

    // Check if we have the required data from navigation state
    const navigationState = location.state as any;
    const hasNavigationData = navigationState?.year && navigationState?.remunerationGroup && navigationState?.receivingPpo;

    // If we have navigation data, proceed immediately without any endpoint dependencies
    if (hasNavigationData) {
      console.log('Proceeding with auto-search using navigation state data - bypassing endpoint dependencies');
    } else {
      // Only wait for options if we don't have navigation data
      if (!remunerationGroupOptionsData || remunerationGroupOptionsData.length === 0) {
        console.log('Waiting for remuneration group options to load...');
        return;
      }
    }

    setIsAutoSearching(true);
    setNoDataFound(false);
    setResults(null);

    // Prepare API parameters
    const params = {
      year: parseInt(selectedYear),
      remunerationGroup: selectedRemunerationGroup,
      receivingPpo: selectedReceivingPpo,
    };

    console.log('Auto-searching with params:', params);

    searchEditRemunerationGroups(params, {
      onSuccess: (response) => {
        console.log('Auto-search results:', response.data);
        setIsAutoSearching(false);
        
        // Handle new API structure with upuRgSenderExchangeOfficeDtos
        if (response.data) {
          console.log('Auto-search raw API response data:', response.data);
          
          // Convert upuRgSenderExchangeOfficeDtos to exchangeOffices format
          const exchangeOfficesData = (response.data as any).upuRgSenderExchangeOfficeDtos || [];
          console.log('Auto-search exchange offices from API:', exchangeOfficesData);
          
          // Transform the data to match expected ExchangeOffice interface
          const transformedExchangeOffices = exchangeOfficesData.map((item: any) => ({
            ppo: item.ppo || item.sendingPpo || '-',
            exchangeOffice: item.exchangeOffice || item.exchangeOfficeName || '-',
            quarter1: item.quarter1 ?? false,
            quarter2: item.quarter2 ?? false,
            quarter3: item.quarter3 ?? false,
            quarter4: item.quarter4 ?? false,
            newPpo: item.newPpo || item.ppo || item.sendingPpo || '',
            newExchangeOffice: item.newExchangeOffice || item.exchangeOffice || item.exchangeOfficeName || '',
          }));

          // Create the expected data structure
          const transformedData = {
            rgId: (response.data as any).rgId || (response.data as any).id || 0,
            year: response.data.year || new Date().getFullYear(),
            receivingPpo: response.data.receivingPpo || '',
            exchangeOffices: transformedExchangeOffices,
            notSubjToSampling: response.data.notSubjToSampling ?? false,
          };

          setNoDataFound(false);
          setResults(transformedData);
          showSnackbar('Data loaded successfully from your previous selection!', 'success');
        } else {
          setNoDataFound(true);
          setResults(null);
        }
      },
      onError: (error) => {
        console.error('Error in auto-search:', error);
        setIsAutoSearching(false);
        showSnackbar('Failed to load data automatically. Please try searching manually.', 'warning');
      },
    });
  }, [selectedYear, selectedRemunerationGroup, selectedReceivingPpo, searchEditRemunerationGroups, showSnackbar, location.state]);

  // Use ref to track if we've already synced URL parameters and navigation state
  const hasSyncedParams = useRef(false);

  // Check if user came from edit button (has complete navigation state)
  const isFromEditButton = React.useMemo(() => {
    const navigationState = location.state as any;
    return !!(navigationState?.year && navigationState?.remunerationGroup && navigationState?.receivingPpo);
  }, [location.state]);

  // Initial parameter sync when page loads (URL params + navigation state)
  useEffect(() => {
    if (hasSyncedParams.current || yearOptions.length === 0) {
      return;
    }

    // Get navigation state data
    const navigationState = location.state as any;
    console.log('Navigation state received:', navigationState);
    
    // Priority: Navigation state > URL params > defaults
    if (navigationState?.year) {
      setSelectedYear(navigationState.year.toString());
    } else {
      const yearParam = searchParams.get('year');
      if (yearParam && yearParam !== 'all') {
        setSelectedYear(yearParam);
      }
    }

    // Handle remuneration group - it could be an object with id/name or just a string
    if (navigationState?.remunerationGroup) {
      console.log('Processing remuneration group from navigation state:', navigationState.remunerationGroup);
      if (typeof navigationState.remunerationGroup === 'object' && navigationState.remunerationGroup.id) {
        // If it's an object, use the id
        const groupId = navigationState.remunerationGroup.id.toString();
        console.log('Setting remuneration group ID:', groupId);
        setSelectedRemunerationGroup(groupId);
      } else if (typeof navigationState.remunerationGroup === 'string') {
        // If it's a string, use it directly
        console.log('Setting remuneration group string:', navigationState.remunerationGroup);
        setSelectedRemunerationGroup(navigationState.remunerationGroup);
      }
    } else {
      const remunerationGroupParam = searchParams.get('remunerationGroup');
      if (remunerationGroupParam) {
        setSelectedRemunerationGroup(remunerationGroupParam);
      }
    }

    if (navigationState?.receivingPpo) {
      setSelectedReceivingPpo(navigationState.receivingPpo);
    } else {
      const receivingPpoParam = searchParams.get('receivingPpo');
      if (receivingPpoParam) {
        setSelectedReceivingPpo(receivingPpoParam);
      }
    }

    // If we have all required fields from navigation state, auto-search immediately
    if (navigationState?.year && navigationState?.remunerationGroup && navigationState?.receivingPpo) {
      console.log('All required fields from navigation state, triggering auto-search immediately');
      // Trigger auto-search immediately without waiting for any endpoint data
      setTimeout(() => {
        handleAutoSearch();
      }, 50);
    } else if (navigationState?.year && !navigationState?.remunerationGroup) {
      // If we have year but no remuneration group, show helpful message
      setTimeout(() => {
        showSnackbar('Year selected from previous page. Please select a Remuneration Group and Receiving DO to search.', 'info');
      }, 500);
    }

    hasSyncedParams.current = true;
  }, [location.state, searchParams, yearOptions, handleAutoSearch]);



  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column' }}
      data-testid='edit-remuneration-groups-container'
    >
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='Edit Remuneration Groups'
            subtitle='Remuneration Groups'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='edit-remuneration-groups-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent
            sidebarOpen={sidebarOpen}
            data-testid='edit-remuneration-groups-main-content'
          >
            <ReportContainer data-testid='edit-remuneration-groups-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='edit-remuneration-groups-header'
              >
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 600, color: '#063142' }}
                  data-testid='edit-remuneration-groups-title'
                >
                  Edit Remuneration Groups
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 1 }}
                  data-testid='edit-remuneration-groups-header-actions'
                >
                  {(results || noDataFound) && (
                    <ClearResultsButton
                      variant='outlined'
                      onClick={clearResults}
                      data-testid='edit-remuneration-groups-clear-results-btn'
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Error Alert */}
              {searchError && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => resetSearchError()}>
                  {searchError.message || 'An error occurred while searching remuneration groups'}
                </Alert>
              )}

              {/* Save Error Alert */}
              {(saveError || saveWithoutConfirmationError) && (
                <Alert 
                  severity='error' 
                  sx={{ mb: 2 }} 
                  onClose={() => {
                    resetSaveError();
                    resetSaveWithoutConfirmationError();
                  }}
                >
                  {saveError?.message || saveWithoutConfirmationError?.message || 'An error occurred while saving changes'}
                </Alert>
              )}

              {/* Filter Section */}
              <FilterSection>
                {/* Year Selector */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      Year: *
                    </Typography>
                    <SelectContainer>
                      <FormControl size='small' fullWidth>
                        <Select
                          value={selectedYear}
                          onChange={(e) => setSelectedYear(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 184 }}
                          disabled={isAutoSearching}
                        >
                          <MenuItem value=''>Select</MenuItem>
                          {yearOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>
                </Box>

                {/* Remuneration Group Selector */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      Remuneration Group: *
                    </Typography>
                    <SelectContainer>
                      <FormControl size='small' fullWidth>
                        <Select
                          value={selectedRemunerationGroup}
                          onChange={(e) => setSelectedRemunerationGroup(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 184 }}
                          disabled={isRemunerationGroupLoading || isAutoSearching}
                        >
                          <MenuItem value=''>
                            {isRemunerationGroupLoading ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <span>Loading...</span>
                              </Box>
                            ) : (
                              'Select'
                            )}
                          </MenuItem>
                          {!isRemunerationGroupLoading &&
                            !remunerationGroupError &&
                            remunerationGroupOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span>{option.label}</span>
                                  {/* Show indicator if this option came from navigation state */}
                                  {location.state?.remunerationGroup && 
                                   typeof location.state.remunerationGroup === 'object' &&
                                   (option.id === location.state.remunerationGroup.id?.toString() || 
                                    option.id === location.state.remunerationGroup.name) && (
                                    <Box
                                      component='span'
                                      sx={{
                                        ml: 1,
                                        bgcolor: '#e8f5e8',
                                        color: '#2e7d32',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        p: '2px 4px',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      (From Selection)
                                    </Box>
                                  )}
                                </Box>
                              </MenuItem>
                            ))}
                          {remunerationGroupError && (
                            <MenuItem disabled>Error loading remuneration groups</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>
                </Box>

                {/* Receiving PPO Selector */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                      Receiving DO: *
                    </Typography>
                    <SelectContainer>
                      <FormControl size='small' fullWidth>
                        <Select
                          value={selectedReceivingPpo}
                          onChange={(e) => setSelectedReceivingPpo(e.target.value)}
                          displayEmpty
                          sx={{ minWidth: 184 }}
                          disabled={isPpoLoading || isAutoSearching}
                        >
                          <MenuItem value=''>
                            {isPpoLoading ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <span>Loading...</span>
                              </Box>
                            ) : (
                              'Select'
                            )}
                          </MenuItem>
                          {!isPpoLoading &&
                            !ppoError &&
                            ppoOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                  }}
                                >
                                  <span>{option.label}</span>
                                 
                                </Box>
                              </MenuItem>
                            ))}
                          {ppoError && <MenuItem disabled>Error loading DOs</MenuItem>}
                        </Select>
                      </FormControl>
                    </SelectContainer>
                  </Box>
                </Box>
              </FilterSection>

              {/* Action Buttons */}
              <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                <SearchButton variant='contained' onClick={handleSearch} disabled={isSearching}>
                  {isSearching ? <CircularProgress size={16} /> : 'SEARCH'}
                </SearchButton>
              </ButtonContainer>

              {/* Results Section - Always show table when search is performed */}
              {(results || noDataFound) && (
                <Box sx={{ mt: 6 }}>
                  {/* Results Table */}
                  <Box>
                    <RemunerationGroupsTable
                      data={results?.exchangeOffices || []}
                      receivingPpo={results?.receivingPpo || selectedReceivingPpo}
                      exchangeOfficeOptions={exchangeOfficeOptionsData || []}
                      isExchangeOfficeLoading={isExchangeOfficeLoading}
                      notSubjToSampling={results?.notSubjToSampling || false}
                      onQuarterChange={handleQuarterChange}
                      onPpoChange={handlePpoChange}
                      onExchangeOfficeChange={handleExchangeOfficeChange}
                      onAddNewRow={handleAddNewRow}
                      onRemoveRow={handleRemoveRow}
                      onSave={handleSave}
                      onNotSubjToSamplingChange={handleNotSubjToSamplingChange}
                      isSaving={isSaving || isSavingWithoutConfirmation}
                    />
                  </Box>
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmationModal.open}
        onClose={handleCancelConfirmation}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">
          Confirm Overwrite
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">
            {confirmationModal.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelConfirmation} 
            color="secondary"
            variant="outlined"
          >
            No
          </Button>
          <Button 
            onClick={handleConfirmedSave} 
            color="primary" 
            variant="contained"
            disabled={isSavingWithoutConfirmation}
          >
            {isSavingWithoutConfirmation ? <CircularProgress size={16} /> : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Modal for Duplicate Entries */}
      <Dialog
        open={errorModal.open}
        onClose={handleCloseErrorModal}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">
          Duplicate Entry
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">
            {errorModal.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseErrorModal} 
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='edit-remuneration-groups-snackbar'
      />
    </Box>
  );
}

export default EditRemunerationGroups;
